import { Building2, Settings, User, type LucideIcon } from "lucide-react";
import type { FC } from "react";

import { PaneHeader } from "@/components/ui/pane-header";
import { cn } from "@/lib/utils";

export type SettingsSectionId = "personal" | "employer" | "data";

export const SETTINGS_SECTIONS: ReadonlyArray<{
  id: SettingsSectionId;
  label: string;
  icon: LucideIcon;
}> = [
  { id: "personal", label: "Personal Information", icon: User },
  { id: "employer", label: "Employer Information", icon: Building2 },
  { id: "data", label: "Advanced Settings", icon: Settings }
];

/**
 * Picks the visible settings section: a left rail on `md+`, a horizontally
 * scrollable tab strip above the form on narrow widths.
 */
export const SettingsNav: FC<{
  active: SettingsSectionId;
  onSelect: (id: SettingsSectionId) => void;
}> = ({ active, onSelect }) => (
  <nav
    aria-label="Case settings sections"
    className="flex flex-none flex-col border-b border-line-2 bg-surface md:w-56 md:self-stretch md:border-r md:border-b-0"
  >
    <PaneHeader title="Case Settings" className="hidden md:flex" />
    <div
      role="tablist"
      className="flex gap-0.5 overflow-x-auto p-1 md:flex-col md:overflow-visible md:p-2"
    >
      {SETTINGS_SECTIONS.map(({ id, label, icon: Icon }) => {
        const isActive = id === active;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(id)}
            className={cn(
              "flex h-8 shrink-0 items-center gap-2 rounded-md px-2 text-left text-base whitespace-nowrap transition-colors outline-none focus-visible:bg-fill-1 md:w-full",
              isActive
                ? "bg-fill-1 text-fg"
                : "text-fg-2 hover:bg-fill-1 hover:text-fg"
            )}
          >
            <Icon
              className={cn(
                "size-4 shrink-0",
                isActive ? "text-fg" : "text-fg-3"
              )}
              aria-hidden
            />
            <span className="truncate">{label}</span>
          </button>
        );
      })}
    </div>
  </nav>
);
